import { clip, type Clip } from '../clip.ts';
import { TAU, envelope, inOut, inQuad, outBack, outCubic, outQuad, phase, settle, span } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';

/*
 * La Madre de las Sanguijuelas (models/leech-mother.ts). Her clips carry the water: SUB is how far under the
 * pool's surface she lies while it covers her (only the hump of her back and her reared neck show).
 *
 * Submerged: 'idle' (4 s loop: the neck sways, the sucker breathes), 'walk' (a slow swim, a wave down her body),
 * 'attack' (rears and strikes from the water, the bite at 0.62 s), 'hit'.
 * Water drained: 'rise' (1.2 s, up out of the water), 'exposed' (1.8 s loop: lying bare, writhing, the sucker
 * gasping), 'hitBare', 'spit' (rears and spits/bites at 0.68 s), 'sink' (1.2 s, back under), 'stun', 'die'.
 *
 * Conventions: the neck bones grow forward (+Z): −X rears them up, +X bows them down; the tail bones grow back
 * (−Z): +X lifts them. Y turns swing the chain side to side; `mouth` scales the lip and teeth (open > 1).
 */

type B = 'root' | 'body' | 'tail1' | 'tail2' | 'tail3' | 'neck1' | 'neck2' | 'head' | 'mouth';
type P = KeyPose<B>;

/** How deep she lies while the water covers her (model units, the root's drop). */
export const LEECH_SUB = -0.85;
const S = LEECH_SUB;

const under = (b: Parameters<Clip<B>['pose']>[0], t: number, dur: number, sway = 1): void => {
  const p = phase(t, dur) * TAU;
  b.tail1.rot(0, 0.06 * sway * Math.sin(p - 0.8));
  b.tail2.rot(0, 0.08 * sway * Math.sin(p - 1.6));
  b.tail3.rot(0, 0.1 * sway * Math.sin(p - 2.4));
};

const idle = (): Clip<B> =>
  clip<B>('idle', 4, true, (b, t) => {
    const p = phase(t, 4) * TAU;
    b.root.pos(0, S + 0.03 * Math.sin(p * 2), 0);
    b.body.rot(0, 0.03 * Math.sin(p), 0.02 * Math.sin(p + 1));
    b.neck1.rot(-0.18 + 0.05 * Math.sin(p * 2), 0.12 * Math.sin(p), 0);
    b.neck2.rot(-0.1 + 0.04 * Math.sin(p * 2 + 0.6), 0.12 * Math.sin(p + 0.9), 0.04 * Math.sin(p));
    b.head.rot(0.08 + 0.05 * Math.sin(p * 2 + 1.2), 0.16 * Math.sin(p + 1.8), 0);
    b.mouth.scale(1 + 0.06 * Math.sin(p * 2 + 2));
    under(b, t, 4);
  }, (t) => {
    const k = Math.sin(phase(t, 4) * TAU * 2);
    return [1 + 0.012 * k, 1 + 0.01 * k, 1];
  });

const walk = (dur = 2.4): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    b.root.pos(0, S + 0.03 * Math.sin(2 * th), 0);
    b.body.rot(0, 0.1 * Math.sin(th), 0.04 * Math.sin(th - 0.5));
    b.tail1.rot(0.04 * Math.sin(2 * th), 0.2 * Math.sin(th - 0.9));
    b.tail2.rot(0.05 * Math.sin(2 * th - 0.6), 0.26 * Math.sin(th - 1.8));
    b.tail3.rot(0.06 * Math.sin(2 * th - 1.2), 0.32 * Math.sin(th - 2.7));
    b.neck1.rot(-0.2, -0.12 * Math.sin(th + 0.6), 0);
    b.neck2.rot(-0.08, -0.1 * Math.sin(th + 1.2), 0);
    b.head.rot(0.1, 0.08 * Math.sin(th + 1.8), 0);
    b.mouth.scale(1 + 0.04 * Math.sin(2 * th));
  });

const SUBR: P = { root: [0, 0, 0, 0, S, 0] };
const attack = (): Clip<B> =>
  clip<B>('attack', 1.4, false, (b, t) => {
    const rear: P = { ...SUBR, body: [-0.06, 0, 0], neck1: [-0.55, 0, 0], neck2: [-0.35, 0, 0], head: [-0.25, 0, 0], mouth: [0, 0, 0, 0, 0, 0, 1.3] };
    const bite: P = { root: [0, 0, 0, 0, S + 0.05, 0.3], body: [0.08, 0, 0], neck1: [0.35, 0, 0], neck2: [0.3, 0, 0], head: [0.25, 0, 0], mouth: [0, 0, 0, 0, 0, 0, 0.82] };
    timeline(b, t, [
      [0, SUBR],
      [0.42, rear, inOut],
      [0.62, bite, inQuad],
      [0.85, { ...bite, mouth: [0, 0, 0, 0, 0, 0, 0.9] }, outQuad],
      [1.4, SUBR, inOut],
    ], { neck2: 0.03, head: 0.05 });
    under(b, t, 1.4, 0.6);
  }, (t) => {
    const k = Math.max(0, settle(t - 0.62, 3, 8)) * (t > 0.62 ? 1 : 0);
    return [1 - 0.03 * k, 1 + 0.03 * k, 1 + 0.05 * k];
  });

const hitAt = (name: string, y: number): Clip<B> =>
  clip<B>(name, 0.5, false, (b, t) => {
    const q = settle(t, 2.2, 8) * span(t, 0, 0.04, outQuad);
    b.root.pos(0, y, -0.04 * q);
    b.neck1.rot(-0.2 * q, 0.08 * q, 0);
    b.neck2.rot(-0.15 * q);
    b.head.rot(-0.2 * q, -0.1 * q, 0.06 * q);
    b.mouth.scale(1 + 0.25 * Math.max(0, q));
    b.tail2.rot(0.1 * q);
  }, (t) => {
    const q = Math.max(0, settle(t, 2.2, 8));
    return [1 + 0.03 * q, 1 - 0.04 * q, 1];
  });

/** Lying bare on the drained floor, writhing, the sucker gasping (no water to hold her up). */
const writhe = (b: Parameters<Clip<B>['pose']>[0], t: number, dur: number, amt = 1): void => {
  const th = phase(t, dur) * TAU;
  b.body.rot(0.03 * Math.sin(2 * th), 0.12 * amt * Math.sin(th), 0.1 * amt * Math.sin(th + 0.7));
  b.tail1.rot(0.08 * Math.sin(2 * th - 0.5), 0.3 * amt * Math.sin(th - 1.0), 0.06 * Math.sin(th));
  b.tail2.rot(0.12 * Math.sin(2 * th - 1.0), 0.36 * amt * Math.sin(th - 2.0), 0);
  b.tail3.rot(0.18 * Math.sin(2 * th - 1.5) + 0.1, 0.44 * amt * Math.sin(th - 3.0), 0);
  b.neck1.rot(0.18 + 0.12 * Math.sin(2 * th + 0.5), -0.34 * amt * Math.sin(th + 0.4), 0.1 * Math.sin(th));
  b.neck2.rot(0.08 + 0.14 * Math.sin(2 * th + 1.1), -0.3 * amt * Math.sin(th + 1.1), 0);
  b.head.rot(-0.1 + 0.2 * Math.sin(2 * th + 1.7), 0.3 * amt * Math.sin(th + 2.0), 0.15 * Math.sin(th + 0.4));
  b.mouth.scale(1 + 0.18 * Math.max(0, Math.sin(2 * th + 2.4)));
};

const exposed = (dur = 1.8): Clip<B> =>
  clip<B>('exposed', dur, true, (b, t) => writhe(b, t, dur), (t) => {
    const k = Math.sin(phase(t, dur) * TAU * 2);
    return [1 + 0.025 * k, 1 - 0.03 * k, 1 + 0.01 * k];
  });

/** Up out of the water (or back under, `down`): the body heaves, the neck thrashes as the water leaves her. */
const surface = (name: string, down: boolean): Clip<B> =>
  clip<B>(name, 1.2, false, (b, t) => {
    const k = span(t, 0.1, 1.0, down ? inOut : outBack);
    const y = down ? 1 - k : k;
    b.root.pos(0, S * (1 - y), 0);
    writhe(b, t, 1.2, 0.4 + 0.6 * Math.sin(Math.PI * Math.min(1, t / 1.2)));
    b.neck1.rot(-0.25 * Math.sin(Math.PI * Math.min(1, t / 1.2)));
  }, (t) => {
    const q = Math.max(0, settle(t - (down ? 1.0 : 0.6), 2.5, 7)) * (t > (down ? 1.0 : 0.6) ? 1 : 0);
    return [1 + 0.04 * q, 1 - 0.05 * q, 1 + 0.04 * q];
  });

const spit = (): Clip<B> =>
  clip<B>('spit', 1.3, false, (b, t) => {
    const rear: P = { body: [-0.1, 0, 0], neck1: [-0.45, 0, 0], neck2: [-0.4, 0, 0], head: [-0.35, 0, 0], mouth: [0, 0, 0, 0, 0, 0, 1.35] };
    const out: P = { root: [0, 0, 0, 0, 0, 0.18], body: [0.05, 0, 0], neck1: [0.2, 0, 0], neck2: [0.2, 0, 0], head: [0.15, 0, 0], mouth: [0, 0, 0, 0, 0, 0, 1.2] };
    timeline(b, t, [
      [0, {}],
      [0.5, rear, inOut],
      [0.68, out, inQuad],
      [0.9, { ...out, mouth: [0, 0, 0, 0, 0, 0, 0.9] }, outQuad],
      [1.3, {}, inOut],
    ], { head: 0.04 });
    b.head.rot(0.04 * Math.sin(t * 50) * envelope(t, 0.66, 0.7, 0.85, 0.95), 0, 0);
  });

const stun = (): Clip<B> =>
  clip<B>('stun', 2.6, true, (b, t) => {
    const p = phase(t, 2.6) * TAU;
    b.body.rot(0, 0, 0.12);
    b.neck1.rot(0.42, 0.2, 0.08);
    b.neck2.rot(0.3 + 0.04 * Math.sin(p * 2), 0.1, 0);
    b.head.rot(0.15, 0.2 * Math.sin(p), 0.2);
    b.tail1.rot(-0.05, 0.1 * Math.sin(p + 1), 0);
    b.tail2.rot(-0.05, 0.14 * Math.sin(p + 2), 0);
    b.tail3.rot(-0.04 + 0.08 * Math.max(0, Math.sin(p * 3)), 0.16 * Math.sin(p + 3), 0);
    b.mouth.scale(1.15 + 0.05 * Math.sin(p * 2));
  });

/** Death (2.6 s): a last thrash, then she rolls onto her side, the neck flops down and the tail curls. */
const die = (): Clip<B> =>
  clip<B>('die', 2.6, false, (b, t) => {
    const roll: P = { root: [0, 0, 1.1, 0, 0.35, 0], body: [0, 0.2, 0], tail1: [0, 0.35, 0], tail2: [0, 0.45, 0], tail3: [0.1, 0.6, 0], neck1: [0.5, -0.3, 0], neck2: [0.45, -0.2, 0], head: [0.3, 0.1, 0], mouth: [0, 0, 0, 0, 0, 0, 1.25] };
    timeline(b, t, [
      [0, {}],
      [0.35, { neck1: [-0.6, 0.3, 0], neck2: [-0.4, 0.2, 0], head: [-0.4, 0, 0], tail2: [0.2, -0.4, 0], tail3: [0.3, -0.5, 0], mouth: [0, 0, 0, 0, 0, 0, 1.4] }, outCubic],
      [0.8, { neck1: [0.1, -0.4, 0], neck2: [0.2, -0.3, 0], head: [0.2, 0.3, 0], tail2: [0, 0.5, 0], tail3: [0, 0.6, 0], mouth: [0, 0, 0, 0, 0, 0, 1.1] }, inOut],
      [1.6, roll, inQuad],
      [1.85, { ...roll, root: [0, 0, 1.15, 0, 0.33, 0] }, outBack],
      [2.6, { ...roll, mouth: [0, 0, 0, 0, 0, 0, 1.0] }, inOut],
    ], { neck2: 0.04, head: 0.08, tail3: 0.06 });
  }, (t) => {
    const q = Math.max(0, settle(t - 1.6, 2.5, 7)) * (t > 1.6 ? 1 : 0);
    return [1 + 0.05 * q, 1 - 0.06 * q, 1];
  });

/** Clips by library kind (see ../rigs.ts). */
export const leechMotherClips = (): Clip[] => [
  idle(), walk(), attack(), hitAt('hit', S), stun(), die(), exposed(), hitAt('hitBare', 0), surface('rise', false), surface('sink', true), spit(),
] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { leechMother: leechMotherClips };
